import React, { useState, useEffect, useCallback, useRef } from "react";
import TableCardv2 from "../../components/TableCardv2";
import BusinessIcon from "@mui/icons-material/Business";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AddIcon from "@mui/icons-material/Add";
import { Chip, Box, Snackbar, Alert, Fab, Button } from "@mui/material";
import {
    IfPermissao,
    usePermissoes,
    CHAVES_EMPRESAS_CRIAR,
    CHAVES_EMPRESAS_EDITAR,
    CHAVES_GRUPOS_ECONOMICOS_LEITURA,
} from "../../config/Context/PermissoesContext";
import { listarEmpresas, buscarDetalheEmpresa } from "../../services/api-empresas.service";
import ModalCadastroEmpresa from '../../components/CadastroEmpresas/ModalCadastroEmpresa';
import Loading from "../../components/Loading";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ModalGrupoEconomico from '../../components/CadastroEmpresas/ModalGrupoEconomico';
import { debounce } from 'lodash';

export default function Empresas() {
    const { pode } = usePermissoes();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]); // Dados da página atual
    const [totalEmpresas, setTotalEmpresas] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchTerm, setSearchTerm] = useState(''); // Termo de busca
    const [modalOpen, setModalOpen] = useState(false);

    // Estado para guardar a empresa que está sendo editada
    const [editingEmpresa, setEditingEmpresa] = useState(null);

    // Estado para Grupo Econômico
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [selectedMatriz, setSelectedMatriz] = useState(null);

    const [loadingDetalhe, setLoadingDetalhe] = useState(false);

    // Estados para Snackbar de notificações
    const [snackbar, setSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error'); // 'error', 'success', 'warning', 'info'

    const initialLoadDoneRef = useRef(false);

    const showSnackbar = useCallback((message, severity = 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbar(true);
    }, []);

    const handleEdit = useCallback(async (empresa) => {
        const id = empresa.id ?? empresa.id_empresa;
        if (!id) {
            showSnackbar('ID da empresa não encontrado', 'error');
            return;
        }
        setLoadingDetalhe(true);
        try {
            const res = await buscarDetalheEmpresa(id);
            const data = res?.empresa ?? res;
            if (data) {
                setEditingEmpresa({
                    ...data,
                    id: data.id ?? data.id_empresa,
                    id_empresa: data.id_empresa ?? data.id
                });
                setModalOpen(true);
            } else {
                showSnackbar('Não foi possível carregar os dados da empresa', 'error');
            }
        } catch (err) {
            console.error('Erro ao buscar detalhe da empresa', err);
            showSnackbar(err?.response?.data?.erro || 'Erro ao carregar dados da empresa', 'error');
        } finally {
            setLoadingDetalhe(false);
        }
    }, [showSnackbar]);

    const handleNew = useCallback(() => {
        setEditingEmpresa(null);
        setModalOpen(true);
    }, []);

    const handleGroup = useCallback((empresa) => {
        if (!empresa.id_grupo_economico) {
            showSnackbar('Esta empresa não pertence a nenhum grupo econômico.', 'warning');
            return;
        }
        setSelectedMatriz(empresa);
        setGroupModalOpen(true);
    }, [showSnackbar]);

    const loadEmpresas = useCallback(async (pageLimit = 10, pageOffset = 0, buscaParam) => {
        setLoading(true);
        try {
            const busca = buscaParam !== undefined ? buscaParam : searchTerm;
            if (buscaParam !== undefined) setSearchTerm(buscaParam);
            const params = { limit: pageLimit, offset: pageOffset };
            if (busca != null && String(busca).trim() !== '') params.q = String(busca).trim();
            const response = await listarEmpresas(params);
            const rawList = response?.dados != null ? response.dados : (Array.isArray(response) ? response : []);
            const podeVerGrupo = pode([...CHAVES_GRUPOS_ECONOMICOS_LEITURA, ...CHAVES_EMPRESAS_EDITAR]);
            const podeEditarEmpresa = pode(CHAVES_EMPRESAS_EDITAR);

            const formattedData = rawList.map(emp => {
                const hasGroup = !!emp.id_grupo_economico;
                const groupName = emp.nome_grupo_economico || "Sem Grupo";
                const cnpjRaw = emp.cnpj || '';

                const btn = [];
                if (podeVerGrupo) {
                    btn.push({
                        icon: <AccountTreeIcon />,
                        title: "Visualizar Grupo Econômico",
                        disabled: !hasGroup,
                        onClick: () => handleGroup(emp)
                    });
                }
                if (podeEditarEmpresa) {
                    btn.push({
                        icon: <MoreHorizIcon />,
                        title: "Editar",
                        onClick: () => handleEdit(emp)
                    });
                }

                return {
                    ...emp,
                    id: emp.id_empresa ?? emp.id,
                    razao_social: emp.razao_social,
                    cnpj: cnpjRaw.length === 14 ? cnpjRaw.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5") : (emp.cnpj || '-'),
                    tipoDisplay: (
                        <Chip
                            label={hasGroup ? groupName : "Sem Grupo"}
                            size="small"
                            sx={{
                                bgcolor: hasGroup ? '#e3f2fd' : '#f5f5f5',
                                color: hasGroup ? '#1565c0' : '#757575',
                                fontWeight: 'bold'
                            }}
                        />
                    ),
                    tipoEmpresaDisplay: (
                        <Chip
                            label={emp.is_matriz ? "Matriz" : "Filial"}
                            size="small"
                            sx={{
                                bgcolor: emp.is_matriz ? '#e8f5e9' : '#fff3e0',
                                color: emp.is_matriz ? '#2e7d32' : '#e65100',
                                fontWeight: 'bold'
                            }}
                        />
                    ),
                    uf: emp.uf || '-',
                    responsavelDisplay: (
                        <Chip
                            avatar={<Box sx={{ bgcolor: '#4caf50', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 10 }}>{(emp.Responsavel?.nome || emp.Responsavel?.login)?.charAt(0) || '-'}</Box>}
                            label={emp.Responsavel?.nome || emp.Responsavel?.login || "Não definido"}
                            variant="outlined"
                            sx={{ border: 'none', bgcolor: 'transparent' }}
                        />
                    ),
                    statusDisplay: (
                        <Chip
                            label={emp.ativo ? "Ativo" : "Inativo"}
                            size="small"
                            sx={{
                                bgcolor: emp.ativo ? '#e8f5e9' : '#ffebee',
                                color: emp.ativo ? '#2e7d32' : '#c62828',
                                fontWeight: 'bold'
                            }}
                        />
                    ),
                    btn
                };
            });
            setData(formattedData);
            setFilteredData(formattedData);
            setTotalEmpresas(response?.total ?? 0);
            initialLoadDoneRef.current = true;
        } catch (error) {
            console.error("Erro ao listar empresas", error);
            showSnackbar('Erro ao carregar dados de empresas', 'error');
        } finally {
            setLoading(false);
        }
    }, [pode, searchTerm, handleEdit, handleGroup]);

    // Busca server-side: envia termo para a API e recarrega na primeira página
    const handleSearch = (searchValue) => {
        setPage(1);
        loadEmpresas(limit, 0, searchValue ?? '');
    };

    // Debounce da função de busca (1 segundo)
    const debouncedSearch = useCallback(
        debounce((val) => handleSearch(val), 600),
        [limit]
    );

    useEffect(() => {
        loadEmpresas(limit, (page - 1) * limit);
    }, [loadEmpresas, page, limit]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        loadEmpresas(limit, (newPage - 1) * limit); // usa searchTerm do state
    };

    const handleRowsPerPageChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
        loadEmpresas(newLimit, 0); // usa searchTerm do state
    };

    const paginationConfig = {
        serverSideTotal: totalEmpresas,
        serverSidePage: page,
        serverSideRowsPerPage: limit,
        onServerPageChange: handlePageChange,
        onServerRowsPerPageChange: handleRowsPerPageChange,
    };

    const columns = [
        { key: "razao_social", label: "Razão Social", width: "20%" },
        { key: "cnpj", label: "CNPJ", width: "12%" },
        { key: "tipoEmpresaDisplay", label: "Tipo", width: "8%" },
        { key: "tipoDisplay", label: "Grupo Econômico", width: "16%" },
        { key: "uf", label: "UF", width: "5%" },
        { key: "responsavelDisplay", label: "Responsável Comercial", width: "18%" },
        { key: "statusDisplay", label: "Status", width: "13%" }
    ];

    const header = {
        icon: <BusinessIcon sx={{ fontSize: 30 }} />,
        title: "Empresas"
    };

    const headerNovaEmpresa = (
        <IfPermissao anyOf={CHAVES_EMPRESAS_CRIAR} fallback={null}>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNew}
                sx={{
                    textTransform: "none",
                    bgcolor: "#0d47a1",
                    "&:hover": { bgcolor: "#1565c0" },
                }}
            >
                Nova empresa
            </Button>
        </IfPermissao>
    );

    return (
        <div style={{ padding: '20px' }}>
            <TableCardv2
                header={header}
                data={filteredData}
                columns={columns}
                loading={loading && !initialLoadDoneRef.current}
                loadingTable={loading && initialLoadDoneRef.current}
                debounce={debouncedSearch}
                searchPlaceholder="Busca por CNPJ ou Razão Social"
                paginationConfig={paginationConfig}
                complementaryHeaderBottom={
                    <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>{headerNovaEmpresa}</Box>
                }
            />

            <ModalCadastroEmpresa
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={() => { setModalOpen(false); loadEmpresas(limit, (page - 1) * limit); }}
                initialData={editingEmpresa}
                onShowNotification={showSnackbar}
            />

            <ModalGrupoEconomico
                open={groupModalOpen}
                onClose={() => setGroupModalOpen(false)}
                matriz={selectedMatriz}
            />

            <IfPermissao anyOf={CHAVES_EMPRESAS_CRIAR} fallback={null}>
                <Fab
                    color="primary"
                    aria-label="Nova empresa"
                    onClick={handleNew}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#0d47a1',
                        '&:hover': {
                            backgroundColor: '#1565c0',
                        },
                    }}
                >
                    <AddIcon />
                </Fab>
            </IfPermissao>

            {/* Snackbar para notificações */}
            <Snackbar
                open={snackbar}
                autoHideDuration={5000}
                onClose={() => setSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={() => setSnackbar(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        '& .MuiAlert-icon': {
                            fontSize: '1.5rem'
                        }
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}
